import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DEF_PG_SIZE_SUB_MIN } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import EnrolmentColumn from '../../models/ColumnDefinitions/EnrolmentColumns';
import EnrolmentRelationshipColumns from '../../models/ColumnDefinitions/EnrolmentRelationshipColumns';
import { ListData } from '../../models/ListData';
import { ListQuery } from '../../models/ListQuery';
import LookupTypes from '../../models/LookupTypes';
import EnrolmentRelationshipSearch from '../../models/search/EnrolmentRelationshipSearch';
import EnrolmentSearch from '../../models/search/EnrolmentSearch';
import { hideColumns } from '../../models/utilities/columnHelper';
import { getCompanyNamesWithPagination } from '../../services/companyService';
import { getDepartmentNamesWithPagination } from '../../services/departmentService';
import { assignUnassignRelationship, getEnrolmentRelationships } from '../../services/enrolmentRelationshipService';
import { getEnrolmentNamesWithPagination, getEnrolmentsExcluding } from '../../services/enrolmentService';
import { getSiteNamesWithPagination } from '../../services/siteService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { appendFilter } from '../../utilities/filters';
import { useDialog } from '../dialog/DialogContext';
import TableTab from '../tabs/table-tab';
import { getCategoryNameWithPagination } from '../../services/categoryService';
import CategorySearch from '../../models/search/CategorySearch';
import AssignModalTwo from '../assignWizard/AssignModal';
import ManageButton from '../assignWizard/ManageButton';
import { PaginationModel } from '@/models/Pagination';

type Props = {
    itemId: string,
    setBusy: (busy: boolean) => void,
    updateBadgeTotal: (updateBadge: boolean, selectedIdsLength: number) => void;
    siteId?: string;
}

const AssignRelationships: FC<Props> = ({ itemId, setBusy, updateBadgeTotal, siteId }) => {
    const { translate } = useLocale();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [currentTotal, setCurrentTotal] = useState<number>(0);
    const dialog = useDialog();

    const getEnrolmentRelationshipFilters = (currentFilters: any) => {
        let filters = appendFilter(itemId, 'Master', 'master.id', currentFilters);
        return appendFilter('0', 'Master Historical', 'maHistorical', filters);
    }

    const lookup = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        dataRefresh((query) => getAssignedEnrolmentRelationships(query));
    }, [showManagedModal, itemId])

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {
        try {
            let pageNumber = 1, pagination: PaginationModel, assignedIds: string[] = [], rows: any[] = [];
            do {
                const enrolmentRelationships = (await getEnrolmentRelationships({ filters: getEnrolmentRelationshipFilters([]), pageSize: 100, goToPage: pageNumber }));

                if (enrolmentRelationships) {
                    assignedIds = assignedIds.concat(enrolmentRelationships.rows.map(relationship => relationship.masterByMasterAssetId));
                    rows = rows.concat(enrolmentRelationships.rows);
                }
            
                pagination = enrolmentRelationships.pagination;
                pageNumber++;
            } while(pageNumber <= pagination.totalPages);
            setSelectedIds(assignedIds);
            setSelectedRows(rows);
            setOriginalSelectedIds(assignedIds);
            setOriginalSelectedRows(rows);
            setCurrentTotal(assignedIds.length);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const handleSubmit = async (toBeAdded: string[], toBeRemoved: string[]) => {
        setBusy(true);

        assignUnassignRelationship(toBeAdded, toBeRemoved, itemId.toString())
            .then(() => {
                const newTotal = currentTotal + toBeAdded.length - toBeRemoved.length;
                updateBadgeTotal(true, newTotal);
                setCurrentTotal(newTotal);
                dataRefresh((query) => getAssignedEnrolmentRelationships(query));
            }).catch((err) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) })
            }).finally(() => {
                setBusy(false);
            })
    }

    const getAssignedEnrolmentRelationships = (listParameters) => {
        return getEnrolmentRelationships({ ...listParameters, filters: getEnrolmentRelationshipFilters(listParameters?.filters) });
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <>
        <TableContextProvider
            dataMethod={(listParameters) => getAssignedEnrolmentRelationships(listParameters)}
            tableDescriptor={EnrolmentRelationshipColumns}
            searchDescriptor={EnrolmentRelationshipSearch}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            manualDataRefresh={manualDataRefresh}
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            lookup={lookup}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedRelationships)}
                setShowModal={setShowManagedModal}
                selectRows={false}
                cyName='assignRelationships'>
                <ManageButton onClick={() => setShowManagedModal(true)} />
            </TableTab>
        </TableContextProvider>
        <AssignModalTwo
            isOpen={showManagedModal}
            dataMethod={(listParameters) => getEnrolmentsExcluding(translate, itemId, { ...listParameters, filters: appendFilter(siteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            onClose={() => { setShowManagedModal(false) }}
            modalTitle={translate.byKey(TranslationKeys.assignRelationships)}
            assigningTitle={translate.byKey(TranslationKeys.relationships)}
            searchColumns={EnrolmentSearch}
            tableColumns={hideColumns(EnrolmentColumn, ['totalTags', 'category'])}
            getAssignedItems={getAssignedItems}
            handleSubmit={handleSubmit} />
    </>
}

export default AssignRelationships;
